import type { DownloadIdentity } from './downloadIdentity';
import type { KeldaPathOps } from './keldaPathOps';
import type { LocalMediaStateWithMediaDestination, LocalStateOps } from './localStateOps';
import type { OngoingDownloadOps } from './ongoingDownloadOps';
import { getOngoingDownloadId } from '../model/ongoingDownload';
import type { Subclip } from '../model/subclip';
import type { ItemWithSourceMedia } from '../upload/premiereItemTypes';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { SEGMENTED_HIGHRES_EXTENSION } from '@mimir/common/dto/mediaDtos';
import { TransferStateDto, type ItemArtefact } from '@mimir/common/dto/transferRequestDtos';
import { artifactReferenceNeedsUniqueFilenamePerInstance } from '@mimir/primitive/artifactReference';
import type { ArtifactReference, AudioTsSegmentRange, TsSegmentRange } from '@mimir/primitive/artifactReference';
import type { Logger } from '@mimir/common/types/logger';
import { getLocalFileSize, isFileAvailableLocally } from '@mimir/common/util/fs';
import { invariant } from '@mimir/common/util/invariant';
import { getEditProxyExtension } from '@mimir/common/util/item';
import { getLocalSegmentedMediaStates, PartialMediaManager } from '@mimir/common/util/media';
import type { LocalSegmentedMediaState } from '@mimir/common/util/mediaState';
import { pathExtension } from '@mimir/common/util/path';
import isEmpty from 'lodash/isEmpty';
import { getMediaDestination, type MediaDestination } from '@mimir/common/kelda/mediaDestination';
import { getBasename } from '@mimir/common/platformAbstraction/pathComposition';
import type { LimitedNodeApi } from '@mimir/common/nodejs/limitedNodeApi/nodeApi';


interface DownloadPaths {
  mediaDestination: MediaDestination;
  segmentedMediaState?: LocalSegmentedMediaState;
}

interface LocalFileAvailable {
  isAvailableLocally: true;
  localPath: string;
}

interface LocalFileNotAvailable {
  isAvailableLocally: false;
}


type LocalFileAvailability = LocalFileAvailable | LocalFileNotAvailable;


export class DefaultLocalStateOps implements LocalStateOps {

  constructor(
    private applicationContext: AdobePanelContext | ElectronContext,
    private logger: Logger,
    private ongoingDownloadOps: OngoingDownloadOps,
    private keldaPathOps: KeldaPathOps,
    private downloadPath: string,
  ) {}


  getAafLocalPaths(item: Pick<ItemWithSourceMedia, 'id'>): MediaDestination {
    return getAafLocalPaths(this.downloadPath, this.applicationContext, item);
  }


  async getItemArtefactLocalMediaState(
    dlId: DownloadIdentity,
    item: ItemWithSourceMedia | null,
    subclip: Subclip | null,
  ): Promise<LocalMediaStateWithMediaDestination> {
    const services = {
      applicationContext: this.applicationContext,
      logger: this.logger,
    };
    let localPaths: DownloadPaths | undefined;
    let localFileAvailability: LocalFileAvailability | undefined;
    let mediaDestination: MediaDestination | undefined;

    // Since artifactReferenceSupportFullFile allows regular non-TsSegmentRange items which
    // should never have an MXF proxy, we preface the condition with `type === 'TsSegmentRange'`
    // to avoid unnecessary disk access to look for MXF proxy for regular items.

    if (dlId.artifactReference.type === 'TsSegmentRange' && artifactReferenceSupportFullFile(dlId.artifactReference) && dlId.artefact === 'proxy') {
      // Prefer `-proxy.mxf` over `proxy-tsrSTART,END.ts` whenever we have it available.
      // One reason is that we don't need to construct a .ts file by downloading segments.
      // Another reason is that the growing MXF proxy will also allow to expand the duration of
      // it in the sequence beyond the duration it had at the time of import (if you import an S3 .ts
      // proxy at a point in time when the recording is only 5 minutes long then the project item
      // in Premiere will remain 5 minutes long forever)
      const dlDir = this.downloadPath;
      const sep = this.applicationContext.pathSep;
      mediaDestination = getMediaDestination(dlDir, `${dlId.itemId}-proxy.mxf`, sep);

      const mxfProxyAvailability = await getLocalFileAvailability(services, [mediaDestination.legacyDestinationPath, mediaDestination.subfoldersDestinationPath]);
      if (mxfProxyAvailability.isAvailableLocally) {
        // Set the paths and availability objects so that we don't need to access the disk
        // again later in the code.
        localPaths = {
          mediaDestination,
        };
        localFileAvailability = mxfProxyAvailability;
      }
    }

    // If these were not set by the code above, it means either we are dealing with an artifact
    // that is not supposed to have mxf proxy, or the mxf proxy is just not available, e.g. from
    // a different machine. Either case, we need to fall back to the regular code path.
    if (!localPaths) {
      localPaths = await getLocalPaths(services, this.downloadPath, dlId, item, subclip);
    }
    if (!localFileAvailability) {
      localFileAvailability = await getLocalFileAvailability(services, [localPaths.mediaDestination.legacyDestinationPath, localPaths.mediaDestination.subfoldersDestinationPath]);
    }

    if (item && !localFileAvailability.isAvailableLocally) {
      // If file couldn't be found in the download locations, look for the file in kelda mapped paths
      if (artifactReferenceSupportFullFile(dlId.artifactReference)) {
        const keldaPath = await this.keldaPathOps.getLocalKeldaMappedPath(item, dlId.artefact, dlId.artifactReference, isFileAvailableLocally);
        if (keldaPath) {
          localFileAvailability = await getLocalFileAvailability(services, [keldaPath]);
        }
      }
    }

    let isBeingDownloaded = false;

    if (!localFileAvailability.isAvailableLocally) {
      // dlId always has a fully resolved artifact reference (ImplicitReference was removed),
      // so getOngoingDownloadId reliably matches the ongoing download entry.
      const odId = getOngoingDownloadId(dlId);
      const ongoingDownload = this.ongoingDownloadOps.getOngoingDownload(odId);
      if (ongoingDownload) {
        const isRestoring = !isEmpty(ongoingDownload.restoreStatus);
        isBeingDownloaded = ongoingDownload.transferState !== TransferStateDto.COMPLETE &&
          ongoingDownload.transferState !== TransferStateDto.ERROR && !isRestoring;
      }
    }

    const result: LocalMediaStateWithMediaDestination = {
      ...localFileAvailability,
      mediaDestination: localPaths.mediaDestination,
      isBeingDownloaded,
      isStateRefreshing: false,
      segmentedMediaState: localPaths.segmentedMediaState ?? undefined
    };

    return result;
  }

  async getPathLocalMediaState(path: string) {
    const isAvailableLocally = await isFileAvailableLocally(this.applicationContext.nodeApi, this.logger, path);
    if (!isAvailableLocally) {
      return {
        localPath: path,
        isAvailableLocally: false,
      };
    }

    return {
      localPath: path,
      isAvailableLocally: true,
      fileSize: getLocalFileSize(this.applicationContext.nodeApi, path),
    };
  }

  setDownloadPath(path: string) {
    this.downloadPath = path;
  }

}


function artifactReferenceSupportFullFile(artifactReference: ArtifactReference): boolean {
  if ((artifactReference.type === 'TsSegmentRange') && (artifactReference.segmentTimes.first > 0)) {
    return false;
  }
  return true;
}


async function getLocalFileAvailability(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  localPaths: string[],
): Promise<LocalFileAvailability> {
  for (const localPath of localPaths) {
    if (await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, localPath)) {
      return {
        isAvailableLocally: true,
        localPath,
      };
    }
  }
  return {
    isAvailableLocally: false,
  };
}


export function getFileNameExtensionFromDownloadIdentity(
  artifactReference: AudioTsSegmentRange | TsSegmentRange,
  artefact: 'proxy' | 'highres',
): string {
  if (artifactReference.type === 'TsSegmentRange' && artifactReference.highResExt !== undefined && artefact === 'highres') {
    return artifactReference.highResExt;
  }
  if (artifactReference.remuxTo === 'fmp4') {
    return artifactReference.type === 'TsSegmentRange' ? '.mp4' : '.m4a';
  }

  return '.ts';
}


export async function getLocalPaths(
  services: {
    applicationContext: {
      clientInstanceId: string;
      pathSep: string;
      nodeApi: LimitedNodeApi,
    },
    logger: Logger,
  },
  downloadPath: string,
  dlId: DownloadIdentity,
  item: ItemWithSourceMedia | null,
  subclip: Subclip | null
): Promise<DownloadPaths> {
  switch (dlId.artifactReference.type) {
    case 'FlatFileReference':
      invariant(item, 'Need item to resolve FlatFileReference');
      return {
        mediaDestination: getArtefactLocalPaths(downloadPath, services.applicationContext, item, dlId.artefact),
      };
    case 'TruncatedMp4': {
      invariant(item, 'Need item to resolve TruncatedMp4');
      let mediaDestination = getArtefactLocalPaths(downloadPath, services.applicationContext, item, dlId.artefact);
      const endTime = subclip !== null ? subclip.endTime : undefined;
      const segmentedMediaState = dlId.artefact === 'proxy' || dlId.artefact === 'highres'
        ? await getLocalSegmentedMediaStates(
            item,
            dlId.artefact,
            new PartialMediaManager(
              services.applicationContext.nodeApi,
              services.logger,
              item,
              downloadPath,
              services.applicationContext.pathSep,
              dlId.artefact,
            ),
            endTime
          )
        : undefined;
      if (segmentedMediaState?.recordPartialMediaInfo) {
        mediaDestination = segmentedMediaState.partialLocalPaths;
      }
      return { mediaDestination, segmentedMediaState };
    }
    case 'AudioTsSegmentRange':
    case 'TsSegmentRange':
      if (subclip) {
        services.logger.warn(`Should not use a subclip parameter (${JSON.stringify(subclip)}) when using TsSegmentRange (${JSON.stringify(dlId.artifactReference)})`);
      }
      invariant(dlId.artefact !== 'aaf', 'Should not have aaf artefact');
      const extension = getFileNameExtensionFromDownloadIdentity(dlId.artifactReference, dlId.artefact);
      return getIdentityBasedLocalPaths(downloadPath, services.applicationContext, extension, dlId);
  }
}



function getIdentityBasedLocalPaths(
  downloadPath: string,
  applicationContext: {
    clientInstanceId: string;
    pathSep: string;
  },
  ext: string,
  dlId: DownloadIdentity,
): DownloadPaths {

  let idStr = getOngoingDownloadId(dlId);
  if (artifactReferenceNeedsUniqueFilenamePerInstance(dlId.artifactReference)) {
    idStr = `${idStr}-${applicationContext.clientInstanceId}`;
  }

  return {
    mediaDestination: getMediaDestination(downloadPath, idStr + ext, applicationContext.pathSep),
    segmentedMediaState: undefined,
  };
}


function getArtefactLocalPaths(downloadPath: string,
    applicationContext: {
      clientInstanceId: string;
      pathSep: string;
      nodeApi: LimitedNodeApi,
    }, item: ItemWithSourceMedia, artefact: ItemArtefact): MediaDestination {
  if (artefact === 'proxy') {
    return getProxyLocalPaths(downloadPath, applicationContext, item);
  } else if (artefact === 'highres') {
    return getHighResLocalPaths(downloadPath, applicationContext, item);
  } else {
    return getAafLocalPaths(downloadPath, applicationContext, item);
  }
}


function getProxyLocalPaths(downloadPath: string, applicationContext: { clientInstanceId: string; pathSep: string; nodeApi: LimitedNodeApi }, item: Pick<ClientItem, 'id' | 'editProxyFormat'>): MediaDestination {
  return getMediaDestination(downloadPath, `${item.id}-proxy${getEditProxyExtension(item)}`, applicationContext.pathSep);
}

export function getFileName(item: Pick<ItemWithSourceMedia, 'originalFileName' | 'ingestSourceFullPath'>): string | undefined {
  let fileName = item.originalFileName;
  if (!fileName && item.ingestSourceFullPath) {
    fileName = getBasename(getBasename(item.ingestSourceFullPath, '/'), '\\');
  }

  return fileName;
}

function getHighResLocalPaths(downloadPath: string, applicationContext: { clientInstanceId: string; pathSep: string; nodeApi: LimitedNodeApi }, item: ItemWithSourceMedia): MediaDestination {
  let highResExtension: string;
  const pathSep = applicationContext.pathSep;
  if (item.media?.codecsByStreamId[StreamRoleId.LiveRecordHighresTs]) {
    highResExtension = SEGMENTED_HIGHRES_EXTENSION;
  } else {
    const fileName = getFileName(item);
    highResExtension =  (!fileName || isEmpty(fileName)) ? '' : pathExtension(pathSep, fileName);
  }
  return getMediaDestination(downloadPath, `${item.id}${highResExtension}`, pathSep);
}


function getAafLocalPaths(downloadPath: string, applicationContext: { clientInstanceId: string; pathSep: string; nodeApi: LimitedNodeApi }, item: Pick<ClientItem, 'id'>): MediaDestination {
  return getMediaDestination(downloadPath, `${item.id}.aaf`, applicationContext.pathSep);
}

